import { useCallback, useEffect, useMemo, useState } from 'react';
import { filterProducts } from '../bundleUtils';

interface FilterConfig {
  VariantStyle: VariantStyle;
  BuildABoxFilterGroups: BuildABoxFilterGroup[];
}

interface UseFiltersResponse {
  filteredProducts: GlobalProduct[] | GlobalVariant[];
  selectedUrlFilterIds: string[];
  setSelectedFilters: (selectedUrlFilterIds: string[]) => void;
  selectedFilters: string[];
  handleResetFilters: (groupFilters: any[]) => void;
}

export const useFilters = ({
  productsInCollection,
  config,
}: {
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  config: FilterConfig;
}): UseFiltersResponse => {
  const selectedUrlFilterIds = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      config?.BuildABoxFilterGroups?.map(({ GroupName, Filters }) => {
        const filterValues = params.getAll(GroupName);
        //Get the filter ids from the Filter Group based on the url param value and exists on the group
        const filterIds = filterValues.map((filter) => {
          const filterId = Filters.find(
            ({ Name }) => Name.toLowerCase() === filter.toLowerCase(),
          );
          return filterId?.Id;
        });
        return filterIds;
      })
        .flat()
        //Removes undefined and keep the type
        .filter(<T,>(value: T | undefined): value is T => Boolean(value)) || []
    );
  }, [config?.BuildABoxFilterGroups]);

  const allFilters = useMemo(
    () =>
      config?.BuildABoxFilterGroups?.flatMap(
        ({ Filters, Id, FilterConditional }) =>
          Filters.map((filter) => ({
            ...filter,
            groupId: Id,
            FilterConditional,
          })),
      ) || [],
    [config?.BuildABoxFilterGroups],
  );

  const [selectedFilters, setSelectedFilters] =
    useState<string[]>(selectedUrlFilterIds);

  const [filteredProducts, setFilteredProducts] = useState<
    GlobalVariant[] | GlobalProduct[]
  >(productsInCollection);

  useEffect(() => {
    const selectedFiltersConfig = allFilters?.filter(({ Id }) =>
      selectedFilters.includes(Id),
    );

    const newProducts = filterProducts({
      selectedFilters: selectedFiltersConfig,
      products: productsInCollection,
      variantStyle: config.VariantStyle,
    });

    setFilteredProducts(newProducts);
  }, [
    config.BuildABoxFilterGroups,
    config.VariantStyle,
    productsInCollection,
    allFilters,
    selectedFilters,
  ]);

  const handleResetFilters = useCallback(
    (groupFilters: any[]) => {
      const groupFiltersIds = groupFilters.map(({ Id }) => Id);
      const newFilters = selectedFilters.filter(
        (selectedFilterId) => !groupFiltersIds.includes(selectedFilterId),
      );
      setSelectedFilters(newFilters);
    },
    [selectedFilters],
  );

  return {
    filteredProducts,
    selectedUrlFilterIds,
    setSelectedFilters,
    selectedFilters,
    handleResetFilters,
  };
};
